import { Feature } from "@sol/features";
import { UserRole } from "@sol/sdk";
import { EnumDictionary } from "@sol/utils";

export const authenticatedFeatures = [Feature.PROFILE];

/*
 * NOTE: This file is intended for module internal use only.
 * Please rely on features module instead
 */

export const AnonymousFeatures = [Feature.LOGIN, Feature.REGISTER, Feature.RESET_PASSWORD];

export const UserRoleFeatureMap: EnumDictionary<UserRole, Feature[]> = {
    [UserRole.LEARNER]: [
        ...authenticatedFeatures,

        Feature.HOMEPAGE,
        Feature.HOMEPAGE_LEARNER,

        Feature.HOMEPAGE_CLASSROOM_CARD,
        Feature.HOMEPAGE_BRIEFS_CARD,
        Feature.HOMEPAGE_FRAMEWORKS_CARD,
        Feature.HOMEPAGE_METRICS_CARD,
        Feature.HOMEPAGE_RESOURCES_CARD,
        Feature.HOMEPAGE_SCENARIO_CARD,

        Feature.CLASSROOM_METRICS,
        Feature.CLASSROOM_WEBINAR,

        Feature.TOPICS_MESSAGE,
        Feature.TOPICS_WORK_SUBMIT,

        Feature.BRIEFS,
        Feature.BRIEFS_EXPLORE,

        Feature.FOLLOW_UPS,
        Feature.FOLLOW_UPS_CREATE,
        Feature.FOLLOW_UPS_CREATE_LEARNER,
        Feature.FOLLOW_UPS_EDIT,
        Feature.FOLLOW_UPS_EDIT_LEARNER,
        Feature.FOLLOW_UPS_NOTIFICATIONS_LEARNER,

        Feature.PROFILE_SKILLS_VIEW,

        Feature.NOTIFICATIONS,

        Feature.WORKSPACE_GROUP_MESSAGE,
        Feature.WORKSPACE_GROUP_SUBMIT,

        Feature.FOLLOW_UPS,
        Feature.FOLLOW_UPS_EDIT,

        Feature.RESOURCES,
        Feature.RESOURCE_CREATE_LEARNER,

        Feature.MY_WORKSPACE,

        // Menu layout for learner
        Feature.MENU_LAYOUT_LEARNER,
        Feature.MENU_HOME,
        Feature.MENU_FOLLOW_UPS,

        // Classroom selector moved to settings for learner
        Feature.SETTINGS_CLASSROOM_SELECT,

        Feature.REFLECTIVE_ANALYSIS_SUBMIT,
        Feature.CLASSROOM_LEARNER_PROGRESSION,

        Feature.AGENDA,
    ],
    [UserRole.TRAINER]: [
        ...authenticatedFeatures,
        Feature.HOMEPAGE,
        Feature.HOMEPAGE_TRAINER,

        Feature.HOMEPAGE_CLASSROOM_CARD,
        Feature.HOMEPAGE_METRICS_CARD,
        Feature.HOMEPAGE_LEARNERS_CARD,
        Feature.HOMEPAGE_BRIEFS_CARD,
        Feature.HOMEPAGE_FRAMEWORKS_CARD,
        Feature.HOMEPAGE_CLASSROOM_SCOPED_FRAMEWORKS,
        Feature.HOMEPAGE_RESOURCES_CARD,
        Feature.HOMEPAGE_SCENARIO_CARD,

        Feature.SELECT_ACTIVE_CLASSROOM,
        Feature.CLASSROOM_UPDATE_LEARNERS,
        Feature.CLASSROOM_UPDATE,
        Feature.CLASSROOM_METRICS,
        Feature.CLASSROOM_METRICS_TRAINER,
        Feature.CLASSROOM_LEARNERS,
        Feature.CLASSROOM_BRIEFS,
        Feature.CLASSROOM_BRIEFS_MANAGE,
        Feature.CLASSROOM_WEBINAR,
        Feature.CLASSROOM_DOWNLOAD_BALANCE_SHEET,
        Feature.EXPORT_CLASSROOM_STATISTICS,

        Feature.PROFILE_CLASSROOMS_VIEW,

        Feature.NOTIFICATIONS,

        Feature.TOPICS,
        Feature.TOPICS_MESSAGE,
        Feature.TOPICS_WORK_EVAL,

        Feature.BRIEFS,
        Feature.BRIEFS_CREATE,
        Feature.BRIEFS_EDIT,
        Feature.BRIEFS_EXPLORE,
        Feature.BRIEFS_MANAGE,

        Feature.WORKSPACE_GROUP,
        Feature.WORKSPACE_GROUP_MESSAGE,
        Feature.WORKSPACE_GROUP_MANAGE,
        Feature.WORKSPACE_GROUP_CORRECTION,

        Feature.FOLLOW_UPS,
        Feature.FOLLOW_UPS_LIST_LEARNER_SELECT,
        Feature.FOLLOW_UPS_CREATE,
        Feature.FOLLOW_UPS_CREATE_TRAINER,
        Feature.FOLLOW_UPS_EDIT,
        Feature.FOLLOW_UPS_EDIT_TRAINER,

        Feature.RESOURCES,
        Feature.RESOURCE_CREATE_TRAINER,

        Feature.PROFESSIONAL_SITUATION_CREATE,
        Feature.PROFESSIONAL_SITUATION_UPDATE,
        Feature.PROFESSIONAL_SITUATION,
        Feature.PROFESSIONAL_SITUATION_DETAILS,

        Feature.PEDAGOGICAL_SCENARIO_MANAGE,
        Feature.PEDAGOGICAL_SCENARIO_DETAILS,

        Feature.USERS_MANAGE_TUTORS,

        Feature.MENU_HOME,
        Feature.MENU_TRAINER_SPACE,
        Feature.MENU_CLASSROOM_SPACE,
        Feature.MENU_QUICK_LINKS_BRIEF_CREATE,
        Feature.MENU_QUICK_LINKS_PROFESSIONAL_SITUATION_CREATE,
        Feature.MENU_FOLLOW_UPS,
        Feature.MENU_CLASSROOM_DASHBOARD,
        Feature.MENU_AGENDA,

        Feature.FRAMEWORKS,
        Feature.CLASSROOM,
        Feature.CLASSROOM_STATISTICS,

        Feature.AGENDA,
        Feature.AGENDA_EVENT_CREATE_PEDAGOGICAL,
        Feature.AGENDA_EVENT_EDIT_PEDAGOGICAL,
        Feature.AGENDA_EVENT_DELETE_PEDAGOGICAL,
        Feature.AGENDA_EVENT_DUPLICATE_PEDAGOGICAL,
        Feature.AGENDA_EVENT_DRAG_PEDAGOGICAL,

        Feature.SYLLABUS_READ,
    ],
    [UserRole.MANAGER]: [
        ...authenticatedFeatures,
        Feature.HOMEPAGE,
        Feature.HOMEPAGE_MANAGER,

        Feature.HOMEPAGE_CLASSROOM_CARD,
        Feature.HOMEPAGE_FRAMEWORKS_CARD,
        Feature.HOMEPAGE_METRICS_CARD,
        Feature.HOMEPAGE_LEARNERS_CARD,
        Feature.HOMEPAGE_BRIEFS_CARD,
        Feature.HOMEPAGE_SCENARIO_CARD,
        Feature.HOMEPAGE_CLASSROOM_SCOPED_FRAMEWORKS,

        Feature.PROFILE_CLASSROOMS_VIEW,

        Feature.SELECT_ACTIVE_CLASSROOM,
        Feature.CLASSROOM_CREATE,
        Feature.CLASSROOM_UPDATE,
        Feature.CLASSROOM_UPDATE_LEARNERS,
        Feature.CLASSROOM_METRICS,
        Feature.CLASSROOM_METRICS_TRAINER,
        Feature.CLASSROOM_LEARNERS,
        Feature.CLASSROOM_BRIEFS,
        Feature.CLASSROOM_WEBINAR,
        Feature.CLASSROOM_DOWNLOAD_BALANCE_SHEET,
        Feature.EXPORT_CLASSROOM_STATISTICS,

        Feature.TOPICS,

        Feature.WORKSPACE_GROUP,

        Feature.BRIEFS,
        // WIP - To replace
        //Feature.BRIEFS_CREATE,
        Feature.BRIEFS_EXPLORE,

        Feature.PROFESSIONAL_SITUATION_DETAILS,

        Feature.FOLLOW_UPS,
        Feature.FOLLOW_UPS_LIST_LEARNER_SELECT,
        Feature.FOLLOW_UPS_CREATE,
        Feature.FOLLOW_UPS_CREATE_MANAGER,
        Feature.FOLLOW_UPS_EDIT,
        Feature.FOLLOW_UPS_EDIT_MANAGER,

        Feature.USERS_MANAGE_TUTORS,

        Feature.HOMEPAGE_RESOURCES_CARD,
        Feature.RESOURCES,

        Feature.ADMIN,

        Feature.MENU_HOME,
        Feature.MENU_CLASSROOM_SPACE,
        Feature.MENU_CLASSROOM_DASHBOARD,
        Feature.MENU_FOLLOW_UPS,
        Feature.MENU_BRIEFS_EXPLORER,

        Feature.PEDAGOGICAL_SCENARIO_MANAGE_PERIODS,
        Feature.PEDAGOGICAL_SCENARIO_DETAILS,
        Feature.ADMIN_STATISTICS,

        Feature.CLASSROOM_STATISTICS,
        Feature.USE_CLASSROOM_TEMPLATES,

        Feature.AGENDA,
        Feature.AGENDA_EVENT_CREATE_CLASSROOM,
        Feature.AGENDA_EVENT_DUPLICATE_CLASSROOM,
        Feature.AGENDA_EVENT_EDIT_CLASSROOM,
        Feature.AGENDA_EVENT_DELETE_CLASSROOM,
        Feature.AGENDA_EVENT_DRAG_CLASSROOM,
        Feature.SYLLABUS_READ,
    ],
    [UserRole.ADMIN]: [
        ...authenticatedFeatures,
        Feature.ADMIN,
        Feature.HOMEPAGE,
        Feature.USE_CLASSROOM_TEMPLATES,

        Feature.ADMIN_PAGES_MANAGE,

        Feature.FRAMEWORKS_ADMIN_READ,
        Feature.FRAMEWORKS_ADMIN_WRITE,
        Feature.FRAMEWORKS_ADMIN_DUPLICATE,
        Feature.SKILLS_ADMIN_READ,
        Feature.SKILLS_ADMIN_WRITE,
        Feature.SKILL_DOMAINS_ADMIN_READ,
        Feature.SKILL_BLOCKS_ADMIN_READ,
        Feature.LANGUAGES_VIEW,
        Feature.TAGS_VIEW,
        Feature.CATEGORIES_VIEW,
        Feature.ADMIN_STATISTICS,
        Feature.USE_CLASSROOM_TEMPLATES,
    ],
    [UserRole.SUPERADMIN]: [
        ...authenticatedFeatures,
        Feature.ADMIN_PAGES_DELETE,
        Feature.ADMIN_ANNOUNCEMENTS_MANAGE,
        Feature.ADMIN,
        Feature.ADMIN_PAGES_MANAGE,
        Feature.LANGUAGES_VIEW,
        Feature.LANGUAGES_MANAGE,
        Feature.TAGS_VIEW,
        Feature.TAGS_MANAGE,
        Feature.CATEGORIES_VIEW,
        Feature.CATEGORIES_MANAGE,
        Feature.CERTIFICATION_ISSUERS_MANAGE,
        Feature.ADMIN_STATISTICS,
        Feature.HOMEPAGE,
        Feature.TRAINING_TEMPLATES_MANAGE,
        Feature.TRAINING_TEMPLATES_ARCHIVE,
        Feature.USE_CLASSROOM_TEMPLATES,
        Feature.BRIEFS_ADMIN,
        Feature.CURRENT_BRAND_WRITE,

        Feature.FRAMEWORKS_ADMIN_READ,
        Feature.FRAMEWORKS_ADMIN_WRITE,
        Feature.FRAMEWORKS_ADMIN_WRITE_ANY_BRAND,
        Feature.SKILLS_ADMIN_READ,
        Feature.SKILLS_ADMIN_WRITE,
        Feature.SKILLS_ADMIN_WRITE_ANY_BRAND,
        Feature.SKILL_DOMAINS_ADMIN_READ,
        Feature.SKILL_DOMAINS_ADMIN_WRITE,
        Feature.SKILL_BLOCKS_ADMIN_READ,
        Feature.SKILL_BLOCKS_ADMIN_WRITE,
        Feature.FRAMEWORKS_ADMIN_TRANSLATIONS_READ,
        Feature.SKILLS_ADMIN_TRANSLATIONS_READ,
    ],
    [UserRole.TUTOR]: [
        ...authenticatedFeatures,
        Feature.HOMEPAGE,
        Feature.HOMEPAGE_TUTOR,

        Feature.HOMEPAGE_LEARNERS_CARD,
        Feature.HOMEPAGE_FRAMEWORKS_CARD,

        Feature.NOTIFICATIONS,

        Feature.BRIEFS,
        Feature.TOPICS,
        Feature.TOPICS_MESSAGE,

        Feature.WORKSPACE_GROUP,
        Feature.WORKSPACE_GROUP_MESSAGE,

        Feature.FOLLOW_UPS,
        Feature.FOLLOW_UPS_LIST_LEARNER_SELECT,
        Feature.FOLLOW_UPS_CREATE,
        Feature.FOLLOW_UPS_CREATE_TUTOR,
        Feature.FOLLOW_UPS_EDIT,
        Feature.FOLLOW_UPS_EDIT_TUTOR,

        Feature.MENU_HOME,
        Feature.MENU_FOLLOW_UPS,

        Feature.FRAMEWORKS_ACCESS_TUTOR,
        Feature.WORKSPACES_ACCESS_TUTOR,
        Feature.LEARNERS_CARD_ACCESS_TUTOR,
    ],
};

export default UserRoleFeatureMap;
